import type { SortingState } from '@tanstack/react-table';

export interface SortOption {
  value: string;
  label: string;
  sorting: SortingState;
}

/** 選択肢に無い並び順(PCの表の見出しで指定したもの)を表す選択肢の値 */
const CUSTOM_SORT_VALUE = '__custom__';

const toSortValue = (sorting: SortingState) => {
  const [first] = sorting;
  if (!first) return '';
  return `${first.desc ? '-' : ''}${first.id}`;
};

interface SortSelectProps {
  options: readonly SortOption[];
  sorting: SortingState;
  onChange: (sorting: SortingState) => void;
  /** API の既定の並び順と同じ値。見出しで同じ並び順を指定したときも既定の選択肢を選んだ状態にする */
  defaultSortValue?: string;
  className?: string;
}

/**
 * スマホ用の並べ替え。PC は表の見出しで並べ替えるため、選択肢に無い並び順のまま画面幅が変わることがある。
 * そのときは実際と違う選択肢を表示しないよう、「表の見出しで指定した並び順」を選んだ状態で表示する。
 */
export const SortSelect = ({ options, sorting, onChange, defaultSortValue, className }: SortSelectProps) => {
  const current = toSortValue(sorting);
  const normalized = current === defaultSortValue ? '' : current;
  const isCustom = !options.some((option) => option.value === normalized);

  return (
    <select
      aria-label="並べ替え"
      value={isCustom ? CUSTOM_SORT_VALUE : normalized}
      onChange={(e) => {
        const option = options.find((o) => o.value === e.target.value);
        if (option) onChange(option.sorting);
      }}
      className={className}
    >
      {isCustom && (
        <option value={CUSTOM_SORT_VALUE} disabled>
          表の見出しで指定した並び順
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};
